import EntityModel from '@/common/models/entity-model';
import _ from '@/common/helpers';

export class CheckoutPageMarkupStyleModel extends EntityModel {
    static BackgroundColor = 'FFFFFF';

    static Color = '000000';

    hydrate({
        background = CheckoutPageMarkupStyleModel.BackgroundColor,
        color = CheckoutPageMarkupStyleModel.Color,
    } = {}) {
        this.background = background;
        this.color = color;
    }

    getPayload() {
        return {
            background: this.background,
            color: this.color,
        };
    }
}

export class CheckoutPageMarkupModel extends EntityModel {
    static Sections = {
        header: 'header',
        body: 'body',
        orderSummary: 'order-summary',
        button: 'button',
        footer: 'footer',
        thankYou: 'thank-you',
        terms: 'terms',
    };

    hydrate({
        section = CheckoutPageMarkupModel.Sections.header,
        style = null,
        content = '',
        enabled = true,

    } = {}) {
        this.section = section;
        this.style = new CheckoutPageMarkupStyleModel(style || {});
        this.content = content;
        this.enabled = enabled;
    }

    isBlankContent() {
        return _.isBlank(this.content);
        // const dummy = document.createElement('div');
        // dummy.innerHTML = this.content;
        // return _.isBlank(dummy.innerText || dummy.textContent);
    }

    getPayload() {
        const allowEmptyContent = this.section === CheckoutPageMarkupModel.Sections.header;
        if (!allowEmptyContent && this.isBlankContent()) {
            this.content = '&nbsp;';
        }
        return {
            section: this.section,
            style: this.style.getPayload(),
            content: this.content,
            enabled: this.enabled,
        };
    }
}

export class CheckoutPageRedirectModel extends EntityModel {
    hydrate({
        url = null,
        timeout = 0,
    } = {}) {
        this.url = url;
        this.timeout = timeout;
    }

    getPayload() {
        return {
            url: this.url,
            timeout: Number(this.timeout),
        };
    }
}

export class CheckoutPageTaxItemModel extends EntityModel {
    hydrate({
        label = null,
        value = null,
    } = {}) {
        this.label = label;
        this.value = value;
    }

    getPayload() {
        return {
            label: this.label,
            value: Number(this.value),
        };
    }
}

export class CheckoutPageTaxModel extends EntityModel {
    hydrate({
        description = null,
        items = [],
    } = {}) {
        this.description = description;
        this.items = items.map(v => new CheckoutPageTaxItemModel(v));
    }

    getPayload() {
        return {
            description: this.description,
            items: this.items.map(v => v.getPayload()),
        };
    }
}

export class CheckoutPageFieldModel extends EntityModel {
    // The order of the object properties is used as order they shown on the page.
    static Defaults = {
        companyName: {name: 'companyName'},
        phoneNumber: {name: 'phoneNumber'},
        address: {name: 'address'},
        addressLine2: {name: 'addressLine2'},
        city: {name: 'city'},
        region: {name: 'region'},
        country: {name: 'country'},
        postalCode: {name: 'postalCode'},
        couponCode: {name: 'couponCode'},
        quantity: {name: 'quantity', default: 1},
    };

    static Settings = {
        hidden: 'hidden',
        optional: 'optional',
        required: 'required',
    };

    hydrate({
        name,
        setting = CheckoutPageFieldModel.Settings.optional,
        label = null,
        ...otherFields
    } = {}) {
        if (_.isEmpty(name)) {
            throw new Error('Missing field name');
        }

        this.name = name;
        this.setting = setting;
        this.label = label;
        this.default = otherFields.default || null;
    }

    get isCouponCode() {
        return this.name === CheckoutPageFieldModel.Defaults.couponCode.name;
    }

    get prettyLabel() {
        return this.label || _.unCamelCase(this.name);
    }

    getPayload() {
        return {
            name: this.name,
            setting: this.setting,
            label: this.label,
            default: this.default,
        };
    }
}

export class OneTimeOfferModel extends EntityModel {
    hydrate({
        planId = null,
        isTiedToQuantity = false,
    } = {}) {
        this.planId = planId;
        this.isTiedToQuantity = isTiedToQuantity;
    }

    getPayload() {
        return {
            planId: this.planId,
            isTiedToQuantity: this.isTiedToQuantity,
        };
    }
}

export class ThankYouOneTimeOfferModel extends OneTimeOfferModel {
}

export class SuggestedUpgradeModel extends EntityModel {
    hydrate({
        planId = null,
        hasProration = false,
    } = {}) {
        this.planId = planId;
        this.hasProration = hasProration;
    }

    getPayload() {
        return {
            planId: this.planId,
            hasProration: this.hasProration,
        };
    }
}

export default class CheckoutPageModel extends EntityModel {
    static Themes = {
        classic: 'classic',
        longCopy: 'long-copy',
        oneStep: 'one-step',
        repeating: 'repeating-background',
        twoStep: 'two-step',
    };

    static QuantityTypes = {
        selectBox: 'selectbox',
        numericField: 'textinput',
    };

    static LogoAlignments = {
        left: 'left',
        center: 'center',
        right: 'right',
    };

    hydrate({
        id = null,
        oneTimeOffer = null,
        planId = null,
        suggestedUpgrade = null,
        thankYouOneTimeOffer = null,
        urlPathSegment = null,
        websiteId = null,
        name = null,
        redirect = null,
        isCustomCustomerIdAllowed = false,
        isBillingAddressHidden = false,
        isPlanSummaryHidden = false,
        theme = CheckoutPageModel.Themes.classic,
        quantityLabel = null,
        quantityType = CheckoutPageModel.QuantityTypes.selectBox,
        quantityMin = null,
        fields = [],
        taxes = null,
        markup = [
            // default markup sections for advanced themes (getPayload reduces values)
            new CheckoutPageMarkupModel(
                {section: CheckoutPageMarkupModel.Sections.header},
            ),
            new CheckoutPageMarkupModel(
                {section: CheckoutPageMarkupModel.Sections.orderSummary},
            ),
            new CheckoutPageMarkupModel(
                {section: CheckoutPageMarkupModel.Sections.footer},
            ),
            new CheckoutPageMarkupModel(
                {section: CheckoutPageMarkupModel.Sections.terms},
            ),
            new CheckoutPageMarkupModel(
                {section: CheckoutPageMarkupModel.Sections.thankYou},
            ),
        ],
        status = EntityModel.ToggleStatuses.active.name,
        isPreCheckedTerms = false,
        logoAlignment = CheckoutPageModel.LogoAlignments.left,
        createdTime = null,
        updatedTime = null,
    } = {}) {
        this.id = id;
        this.planId = planId;
        this.oneTimeOffer = oneTimeOffer
            ? new OneTimeOfferModel(oneTimeOffer)
            : null;
        this.suggestedUpgrade = suggestedUpgrade
            ? new SuggestedUpgradeModel(suggestedUpgrade)
            : null;
        this.thankYouOneTimeOffer = thankYouOneTimeOffer
            ? new ThankYouOneTimeOfferModel(thankYouOneTimeOffer)
            : null;
        this.urlPathSegment = urlPathSegment;
        this.websiteId = websiteId;
        this.name = name;
        this.redirect = new CheckoutPageRedirectModel(redirect || {});
        this.isCustomCustomerIdAllowed = isCustomCustomerIdAllowed;
        this.isBillingAddressHidden = isBillingAddressHidden;
        this.isPlanSummaryHidden = isPlanSummaryHidden;
        this.theme = theme;
        this.quantityLabel = quantityLabel;
        this.quantityType = quantityType;
        this.quantityMin = quantityMin;
        this.taxes = taxes ? new CheckoutPageTaxModel(taxes) : null;
        this.markup = markup.map(v => new CheckoutPageMarkupModel(v));
        this.status = EntityModel.convertStringStatus(status);
        this.isPreCheckedTerms = isPreCheckedTerms;
        this.logoAlignment = logoAlignment;
        this.createdTime = createdTime;
        this.updatedTime = updatedTime;

        if (!Array.isArray(fields)) {
            if (!_.isEmpty(fields)) {
                throw new Error('Invalid format of the fields');
            }

            fields = [];
        }

        fields = fields.reduce((result, field) => {
            result[field.name] = field;
            return result;
        }, {});

        this.fields = Object
            .values(CheckoutPageFieldModel.Defaults)
            .map(field => new CheckoutPageFieldModel(fields[field.name] || field));
    }

    get isAdvancedTheme() {
        return this.theme !== CheckoutPageModel.Themes.classic;
    }

    get canHidePlanSummary() {
        return this.theme !== CheckoutPageModel.Themes.twoStep;
    }

    get canAlignLogo() {
        return ![
            CheckoutPageModel.Themes.classic,
            CheckoutPageModel.Themes.repeating,
        ].includes(this.theme);
    }

    get hasThankYouOTO() {
        return this.thankYouOneTimeOffer !== null;
    }

    /**
     * Detect whether this page can include suggested upgrade, one-time offer, thank-you offer
     * @return {boolean}
     */
    get canEnableExtras() {
        return this.theme === CheckoutPageModel.Themes.twoStep;
    }

    /**
     * Check whether the specific section exists within the page markup.
     * @param {CheckoutPageMarkupModel.Sections} section
     */
    hasMarkup(section) {
        return this.markup.length && !!this.markup.find(v => v.section === section);
    }

    /**
     * Find the index of the section within the markup collection.
     * @param {string} section
     * @return {number}
     */
    getMarkupIndex(section) {
        return this.markup.length && this.markup.findIndex(v => v.section === section);
    }

    /**
     * Find the index of the section related to the body using its internal ID.
     * @param {string} $internalId
     * @return {number}
     */
    getBodyMarkupIndex({$internalId}) {
        return this.markup.findIndex(v => v.$internalId === $internalId);
    }

    getMarkup({section = null}) {
        if (section) {
            return this.markup.find(v => v.section === section);
        }
        return null;
    }

    addMarkup(section) {
        this.markup.push(section);
    }

    addTaxLine(taxLine) {
        if (!this.taxes) {
            this.taxes = new CheckoutPageTaxModel();
        }
        this.taxes.items.push(taxLine);
    }

    removeMarkup({section = null, index = null}) {
        const position = section
            ? this.markup.findIndex(v => v.section === section)
            : index;
        this.markup.splice(position, 1);
    }

    removeAllMarkup(section) {
        this.markup = this.markup.filter(v => v.section !== section);
    }

    getPayload() {
        let payload = {
            id: this.id,
            name: this.name,
            planId: this.planId,
            urlPathSegment: this.urlPathSegment,
            websiteId: this.websiteId,
            redirect: this.redirect.getPayload(),
            isCustomCustomerIdAllowed: this.isCustomCustomerIdAllowed,
            status: EntityModel.convertBooleanStatus(this.status),
            theme: this.theme,
            fields: [],
            logoAlignment: this.logoAlignment,
        };

        if (this.isAdvancedTheme) {
            payload = {
                ...payload,
                oneTimeOffer: this.oneTimeOffer
                    ? this.oneTimeOffer.getPayload()
                    : {},
                suggestedUpgrade: this.suggestedUpgrade
                    ? this.suggestedUpgrade.getPayload()
                    : {},
                thankYouOneTimeOffer: this.thankYouOneTimeOffer
                    ? this.thankYouOneTimeOffer.getPayload()
                    : {},
                isBillingAddressHidden: this.isBillingAddressHidden,
                isPlanSummaryHidden: this.isPlanSummaryHidden,
                quantityLabel: _.isBlank(this.quantityLabel) ? null : this.quantityLabel,
                quantityType: _.isBlank(this.quantityType) ? null : this.quantityType,
                quantityMin: _.isBlank(this.quantityMin) ? null : Number(this.quantityMin),
                fields: this.fields.map(v => v.getPayload()),
                taxes: this.taxes ? this.taxes.getPayload() : null,
                isPreCheckedTerms: this.isPreCheckedTerms,
                markup: this.markup.map(v => v.getPayload()),
            };
        } else {
            const header = this.getMarkup({section: CheckoutPageMarkupModel.Sections.header});
            if (header) {
                payload.markup = [header.getPayload()];
            }
        }
        return payload;
    }
}
